import { Array, HashSet } from 'effect'

import type { ViewQueryRecordNode, ViewQueryTableNode } from '../../schemas/query/index.ts'

// 占有行数は区間幅と同じ量なので、読む側は meta.height でなく区間から導く。
export const getInnerRowSpan = (record: { meta: { innerRowIndexStart: number; innerRowIndexEnd: number } }): number =>
  record.meta.innerRowIndexEnd - record.meta.innerRowIndexStart

/**
 * 行の高さに数える対象のノード名。1 列も持たないノード (フィルタ・ソート・参照項目の展開だけで木に残るブロック) を
 * 高さから外すために使う。ノードごとに配列を線形探索すると O(ノード数 × 可視ノード数) になるため、集合にして呼び出し側から渡す。
 *
 * 編集後のツリーを読み取り専用で保持する呼び出し元からも渡せるよう、readonly を許容する形で受ける。
 */
export const getVisibleNodeNames = (view: {
  readonly fields: ReadonlyArray<{ readonly nodePath: readonly string[] }>
}): HashSet.HashSet<string> => HashSet.fromIterable(view.fields.flatMap((field) => field.nodePath))

const assignTableGeometry = (
  tableNode: ViewQueryTableNode,
  parentStart: number,
  visibleNodeNames: HashSet.HashSet<string>,
): ViewQueryTableNode => {
  const [, children] = Array.mapAccum(tableNode.children, parentStart, (innerRowIndexStart, record) => {
    const updatedRecord = assignRecordGeometry(record, innerRowIndexStart, visibleNodeNames)
    return [updatedRecord.meta.innerRowIndexEnd, updatedRecord]
  })

  return { ...tableNode, children }
}

const assignRecordGeometry = <T extends ViewQueryRecordNode>(
  record: T,
  innerRowIndexStart: number,
  visibleNodeNames: HashSet.HashSet<string>,
): T => {
  const children = record.children.map((child) => assignTableGeometry(child, innerRowIndexStart, visibleNodeNames))
  const visibleChildHeights = children
    .filter((child) => HashSet.has(visibleNodeNames, child.nodeName))
    .map((child) => child.children.reduce((height, childRecord) => height + getInnerRowSpan(childRecord), 0))
  const height = Math.max(...visibleChildHeights, 1)

  return {
    ...record,
    children,
    meta: {
      ...record.meta,
      height,
      innerRowIndexStart,
      innerRowIndexEnd: innerRowIndexStart + height,
    },
  }
}

/**
 * レコードツリーの内部行ジオメトリ (height と innerRowIndexStart/End) を 1 回の再帰で確定させる。
 *
 * 行 div の高さとセル列の高さはどちらも区間 (innerRowIndexStart/End) から読む。
 * height は旧クライアント向けにレスポンスへ残している値で、区間と同じ値になるよう
 * 子から昇ってきた高さで height を決め、その確定値から innerRowIndexEnd を導く。
 */
export const assignInnerRowGeometry = <T extends ViewQueryRecordNode>(
  record: T,
  visibleNodeNames: HashSet.HashSet<string>,
): T => assignRecordGeometry(record, 0, visibleNodeNames)
